/**
 * The browser session's lifecycle as the auth provider sees it (#1175).
 *
 * A sign-out, in this tab or another, ends the current generation. Work that may adopt a
 * session (a restore, a renewal, a workspace switch, demo mode, an onboarding refresh, a
 * reconcile after another tab renewed) captures the generation when it starts and adopts only
 * while that generation is still current, so nothing that began before a sign-out can undo
 * it. A sign-in is the exception: it captures the generation when its bootstrap lands, so of
 * a sign-in and a sign-out the one that lands last wins. After a sign-out no renewal starts
 * until something adopts a session again.
 */
export interface SessionGeneration {
  /** Aborted when a sign-out ends this generation. */
  readonly signal: AbortSignal;
}

class Generation implements SessionGeneration {
  readonly #controller = new AbortController();

  /** Whether a session was adopted in this generation, which is what admits renewals. */
  public adopted = false;

  public get signal(): AbortSignal {
    return this.#controller.signal;
  }

  public end(): void {
    this.#controller.abort();
  }
}

export class SessionLifecycle {
  #current = new Generation();

  /** The generation work starting now belongs to. */
  public capture(): SessionGeneration {
    return this.#current;
  }

  /** A sign-out: ends the current generation and starts one with no session in it. */
  public end(): void {
    this.#current.end();
    this.#current = new Generation();
  }

  /** Whether `generation` is still current, that is, no sign-out came since it was captured. */
  public isCurrent(generation: SessionGeneration): boolean {
    return generation === this.#current;
  }

  /**
   * Adopts a session for work that captured `generation`, which admits renewals again. False,
   * adopting nothing, when a sign-out ended that generation first.
   */
  public adopt(generation: SessionGeneration): boolean {
    if (!this.isCurrent(generation)) return false;
    this.#current.adopted = true;
    return true;
  }

  /** Whether a renewal may start: a session was adopted and no sign-out has come since. */
  public admitsRenewal(): boolean {
    return this.#current.adopted;
  }
}

/** What `unlessEnded` resolves to once a sign-out has ended the generation. */
export const SESSION_ENDED = Symbol('session-ended');

/** `work`'s result, or SESSION_ENDED as soon as a sign-out ends `generation`, whichever is first. */
export async function unlessEnded<T>(
  generation: SessionGeneration,
  work: Promise<T>,
): Promise<T | typeof SESSION_ENDED> {
  if (generation.signal.aborted) return SESSION_ENDED;
  const ended = new Promise<typeof SESSION_ENDED>((resolve) => {
    generation.signal.addEventListener('abort', () => { resolve(SESSION_ENDED); }, { once: true });
  });
  return await Promise.race([work, ended]);
}
